import { z } from "zod";

import { RequestSource } from "../../constants/session.constants";

import { SubscriptionChangeAction, SubscriptionPauseReason } from "./subscription";

/**
 * Staff pause reason values allowed from the admin dashboard.
 * Mirrors server rule: staff must use `ADMIN_*` pause reasons.
 */
export const subscriptionPauseReasonAdminSchema = z.enum([
  SubscriptionPauseReason.ADMIN_DOG_UNWELL,
  SubscriptionPauseReason.ADMIN_ON_TRIP,
  SubscriptionPauseReason.ADMIN_SHORT_BREAK,
  SubscriptionPauseReason.ADMIN_PAUSE_OTHER,
]);

export type SubscriptionPauseReasonAdmin = z.infer<typeof subscriptionPauseReasonAdminSchema>;

/**
 * Structured pause window (bulk-reschedule).
 * Mirrors `pauseActionDataSchema` in sploot-server `create-subscription-change-request.dto.ts`.
 */
export const pauseActionDataSchema = z.object({
  startDateEpochMs: z
    .number()
    .int()
    .positive({ message: "startDateEpochMs must be a positive epoch millisecond timestamp" }),
  pauseDays: z
    .number()
    .int()
    .min(1, { message: "pauseDays must be at least 1" })
    .max(366, { message: "pauseDays cannot exceed 366" }),
});

export type PauseActionData = z.infer<typeof pauseActionDataSchema>;

/**
 * Fields common to dashboard-originated subscription change requests
 * (payload only — `source` / action-specific fields are added by the client).
 */
export const dashboardSubscriptionChangeRequestBasePayloadSchema = z.object({
  comment: z.string().trim().min(1).optional(),
});

export type DashboardSubscriptionChangeRequestBase = z.infer<
  typeof dashboardSubscriptionChangeRequestBasePayloadSchema
>;

export const dashboardSubscriptionPauseChangeRequestPayloadSchema =
  dashboardSubscriptionChangeRequestBasePayloadSchema.extend({
    reason: subscriptionPauseReasonAdminSchema,
    actionData: pauseActionDataSchema,
  });

export type DashboardSubscriptionPauseChangeRequestInput = z.infer<
  typeof dashboardSubscriptionPauseChangeRequestPayloadSchema
>;

/**
 * Cancellation payload for `createSubscriptionChangeRequest` (dashboard).
 */
export const dashboardSubscriptionCancelChangeRequestPayloadSchema =
  dashboardSubscriptionChangeRequestBasePayloadSchema.extend({
    action: z.literal(SubscriptionChangeAction.CANCEL_SUBSCRIPTION),
    reason: z.string().trim().min(1),
  });

export type DashboardSubscriptionCancelChangeRequestInput = z.infer<
  typeof dashboardSubscriptionCancelChangeRequestPayloadSchema
>;

/**
 * Full POST body for a dashboard-initiated subscription pause change request.
 * Aligns with sploot-server `createSubscriptionChangeRequestSchema` for `action === PAUSE_SUBSCRIPTION`.
 */
export const createDashboardSubscriptionPauseChangeRequestBodySchema =
  dashboardSubscriptionPauseChangeRequestPayloadSchema.extend({
    source: z.literal(RequestSource.DASHBOARD),
    action: z.literal(SubscriptionChangeAction.PAUSE_SUBSCRIPTION),
  });

export type CreateDashboardSubscriptionPauseChangeRequestBody = z.infer<
  typeof createDashboardSubscriptionPauseChangeRequestBodySchema
>;

/**
 * POST body for bulk-reschedule preview (`/users/:userId/subscriptions/:id/bulk-reschedule/preview`).
 * `reason` matches sploot-server `SubscriptionPauseReason` (staff must send `ADMIN_*` values; customers `CUSTOMER_*`).
 */
export const bulkReschedulePreviewRequestSchema = z.object({
  startDate: z.number().int().positive({ message: "startDate must be a positive epoch millisecond timestamp" }),
  pauseDays: z
    .number()
    .int()
    .min(1, { message: "pauseDays must be at least 1" })
    .max(366, { message: "pauseDays cannot exceed 366" }),
  reason: z.nativeEnum(SubscriptionPauseReason),
});

export type BulkReschedulePreviewRequestDto = z.infer<typeof bulkReschedulePreviewRequestSchema>;

export const userBulkReschedulePreviewResultSchema = z.object({
  startDate: z.number().int().positive(),
  rescheduledCount: z.number().int().nonnegative(),
  cancelledWalks: z.number().int().nonnegative(),
  resumeDate: z.number().int().nonnegative(),
});

export type UserBulkReschedulePreviewResultDto = z.infer<typeof userBulkReschedulePreviewResultSchema>;
